import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('feedingTubePreferences');
export interface TemporaryFeedingTubeDetails {
  acceptableCircumstances?: string;
  maxDuration?: string;
  specificConditions?: string;
  discussedWithHealthcareProvider?: boolean;
}

export interface LongTermFeedingTubeDetails {
  warrantsConditions?: string;
  tubeTypePreferences?: string;
  qualityOfLifeFactors?: string;
  ongoingCarePreferences?: string;
}

export interface TrialPeriodFeedingTubeDetails {
  trialDuration?: string;
  continueCriteria?: string;
  evaluationFactors?: string;
  decisionMakingProcess?: string;
}

export interface DeclineFeedingTubeDetails {
  reasonsForDeclining?: string;
  exceptions?: string;
  alternativeNutritionMethods?: string;
  discussedAlternatives?: boolean;
}

export interface FamilyDecideFeedingTubeDetails {
  specificDecisionMaker?: string;
  factorsToConsider?: string;
  circumstancesToKnow?: string;
  valuesDiscussed?: boolean;
}

export type FeedingTubeDetails =
  | TemporaryFeedingTubeDetails
  | LongTermFeedingTubeDetails
  | TrialPeriodFeedingTubeDetails
  | DeclineFeedingTubeDetails
  | FamilyDecideFeedingTubeDetails
  | null;

export interface FeedingTubePreferencesInfoRec {
  id: string;
  userId: string;
  feedingTubeType?: string;
  locationOfDirective?: string;
  specialInstructions?: string;
  feedingTubeDetails?: FeedingTubeDetails;
  notes?: string;
  isLocked?: boolean;
}

class FeedingTubePreferencesInfoService {
  async getAllFeedingTubePreferences(): Promise<FeedingTubePreferencesInfoRec[]> {
    const response = await http.get('/api/feeding-tube-preferences');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch feeding tube preferences information');
    }

    return response.data.data || [];
  }

  async getFeedingTubePreferencesById(id: string): Promise<FeedingTubePreferencesInfoRec | null> {
    try {
      const response = await http.get(`/api/feeding-tube-preferences/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch feeding tube preferences information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching feeding tube preferences information by ID:', error);
      return null;
    }
  }

  async createFeedingTubePreferences(feedingTubeData: Partial<FeedingTubePreferencesInfoRec>): Promise<FeedingTubePreferencesInfoRec> {
    const response = await http.post('/api/feeding-tube-preferences', feedingTubeData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to create feeding tube preferences information');
    }

    return response.data.data;
  }

  async updateFeedingTubePreferences(id: string, feedingTubeData: Partial<FeedingTubePreferencesInfoRec>): Promise<FeedingTubePreferencesInfoRec> {
    const response = await http.patch(`/api/feeding-tube-preferences/${id}`, feedingTubeData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update feeding tube preferences information');
    }

    return response.data.data;
  }

  async deleteFeedingTubePreferences(id: string): Promise<void> {
    const response = await http.delete(`/api/feeding-tube-preferences/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete feeding tube preferences information');
    }
  }

  async lockFeedingTubePreferences(id: string): Promise<void> {
    const response = await http.post(`/api/feeding-tube-preferences/${id}/lock`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock feeding tube preferences information');
    }
  }

  async unlockFeedingTubePreferences(id: string, emails: string[]): Promise<void> {
    const response = await http.post(`/api/feeding-tube-preferences/${id}/unlock`, { emails });

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock feeding tube preferences information');
    }
  }

  async getFeedingTubePreferencesAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/feeding-tube-preferences/${id}/grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to get feeding tube preferences access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching feeding tube preferences access grants:', error);
      throw error;
    }
  }
}

export const feedingTubePreferencesService = new FeedingTubePreferencesInfoService();